import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBlogBySlug } from '../../store/slices/blogSlice';

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { currentBlog: content, loading } = useAppSelector((state) => state.blog);

  useEffect(() => {
    if (id) {
      console.log('BlogDetailPage: Fetching blog with slug:', id);
      dispatch(fetchBlogBySlug(id));
    }
  }, [id, dispatch]);

  console.log('BlogDetailPage: Current state:', { content, loading });

  const page_name = content?.title || 'Blog Detail';
  const text_content = content?.text_content || '';
  const keyword = content?.keyword || '';
  const image_url = content?.image_url || '';
  const og_image = content?.og_image || '';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow"></div>
      </div>
    );
  }

  if (!content) {
    console.log('BlogDetailPage: No content found for slug:', id);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <h2 className="text-2xl font-bold text-jet-gray-800 mb-4">Blog Post Not Found</h2>
        <p className="text-gray-600 mb-4">The blog post with slug "{id}" could not be found.</p>
        <button onClick={() => navigate('/blog')} className="bg-jet-yellow px-6 py-2 rounded-md font-medium">
          Back to Blogs
        </button>
      </div>
    );
  }

  console.log(content,"++++content")

  return (
    <>
      {/* Meta tags matching PHP structure */}
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="robots" content="index, follow" />
        <meta name="title" content={page_name} />
        <meta name="description" content={text_content} />
        <meta name="keywords" content={keyword} />
        <meta property="og:title" content={page_name} />
        <meta property="og:description" content={text_content} />
        <meta property="og:image" content={og_image} />
        <meta property="og:image_url" content={image_url} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Jetscanner360" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page_name} />
        <meta name="twitter:description" content={text_content} />
        <meta name="twitter:image_url" content={image_url} />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:creator" content="@yourtwitterhandle" />
        <link rel="canonical" href={window.location.href} />
        <meta name="theme-color" content="#ffffff" />
        <link rel="icon" href="/assets/images/fav.svg" type="image/x-icon" />
        <title>{page_name}</title>
        <link rel="image_src" href={image_url} />
        <link rel="apple-touch-icon" href="/assets/images/fav.svg" />
      </head>

      <section className="bg-gray-100 min-h-screen" style={{ paddingTop: '80px' }}>
        <div className="w-[86%] mx-auto py-6">
          {/* White Outer Container */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header with Title, Author, and Back Button */}
            <div className="p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                    {content.title}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <i className="fas fa-user text-[#ffd200]"></i>
                      <span className="font-semibold">{content.created_by || 'Jamie C'}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="fas fa-calendar text-[#ffd200]"></i>
                      <span>{formatDate(content.created_at)}</span>
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleBack}
                  className="bg-[#ffd200] hover:bg-[#e6bd00] text-black font-semibold px-6 py-2.5 rounded-md transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa fa-arrow-left"></i>
                  <span>Back</span>
                </button>
              </div>

              {/* Large Hero Image */}
              {(content.image_url || image_url) && (
                <div className="w-full overflow-hidden mb-8">
                  <img 
                    src={`${content.image_url}`} 
                    alt={content.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/hero-image.jpeg';
                    }}
                    className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
                  />
                </div>
              )}

              {/* Blog Content */}
              <div className="blog-content prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: content.page_content }} />
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .blog-content {
          font-size: 18px;
          line-height: 1.8;
          color: #333;
        }
        
        .blog-content p {
          margin: 1.2em 0;
          font-size: 18px;
          line-height: 1.8;
        }
        
        .blog-content ul {
          list-style: disc;
          margin-left: 20px;
          margin-bottom: 1.2em;
        }
        
        .blog-content ol {
          padding-left: 20px;
          margin-bottom: 1.2em;
        }
        
        .blog-content li {
          margin: 0.5em 0;
        }
        
        .blog-content h1 {
          font-size: 32px;
          font-weight: bold;
          margin: 1.5em 0 0.8em 0;
          color: #111;
        }
        
        .blog-content h2 {
          font-size: 28px;
          font-weight: bold;
          margin: 1.3em 0 0.7em 0;
          color: #111;
        }
        
        .blog-content h3 {
          font-size: 24px;
          font-weight: bold;
          margin: 1.2em 0 0.6em 0;
          color: #111;
        }
        
        .blog-content h4 {
          font-size: 20px;
          font-weight: bold;
          margin: 1em 0 0.5em 0;
          color: #111;
        }
        
        .blog-content h5 {
          font-size: 18px;
          font-weight: bold;
          margin: 1em 0 0.5em 0;
          color: #111;
        }
        
        .blog-content h6 {
          font-size: 16px;
          font-weight: bold;
          margin: 1em 0 0.5em 0;
          color: #111;
        }
        
        .blog-content strong,
        .blog-content .highlight {
          font-weight: 600;
          color: #111;
        }
        
        .blog-content a {
          color: #ffd200;
          text-decoration: underline;
        }
        
        .blog-content a:hover {
          color: #e6bd00;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          margin: 1.5em 0;
          border-radius: 8px;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #ffd200;
          padding-left: 1.5em;
          margin: 1.5em 0;
          font-style: italic;
          color: #555;
        }
        
        .blog-content code {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.9em;
        }
        
        .blog-content pre {
          background: #f5f5f5;
          padding: 1em;
          border-radius: 6px;
          overflow-x: auto;
          margin: 1.5em 0;
        }
        
        .blog-content pre code {
          background: none;
          padding: 0;
        }
      `}</style>
    </>
  );
};

export default BlogDetailPage;